import { FC } from 'react'
import {
  Box,
  Button,
  DatePicker,
  makeStyles,
  MuiStack,
  PhoneField,
  TextField,
  Typography,
} from '@perk-ui/core'
import { endOfYesterday, format, getYear, isPast, isToday } from 'date-fns'
import { useFormik } from 'formik'
// import { useHistory } from 'react-router'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'

import { useSitesContext } from '../../../components/SitesProvider'
import useVerifyPatientData from '../../../features/query-hooks/useVerifyPatientData'
import useDates from '../../../hooks/useDates'
import PatientSelfOnboardUrls from '../PatientSelfOnboardUrls'

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'DM Sans',
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: '24px',
    textAlign: 'center',
    marginBottom: '40px',
  },
  inputStack: {
    '& .MuiTextField-root': {
      marginBottom: '14px',
    },
  },
  goBackButton: {
    border: '1px solid #90CAF9', // Light blue border
    borderRadius: '8px', // Rounded corners
    color: '#42A5F5 !important', // Blue text color
    textColor: '#42A5F5', // Blue text color
    textTransform: 'none', // Prevents all-uppercase text
    // fontWeight: 500, // Semi-bold text
    backgroundColor: 'white', // White background
    '&:hover': {
      backgroundColor: '#E3F2FD', // Light blue background on hover
    },
  },
}))

interface VerifyPatientForm {
  firstName: string
  lastName: string
  cellPhoneLocal: string
  dateOfBirth: string
}

interface VerifyPatientProps {}

const VerifyPatient: FC<VerifyPatientProps> = () => {
  const verifyPatientData = useVerifyPatientData()
  const classes = useStyles()
  const { localizedParseDate, localizedDatePattern } = useDates()

  const { currentSite } = useSitesContext()

  const { push, goBack } = useHistory()

  const country = currentSite?.country
  const isUsa = country === 'united_states_of_america'

  const patientValidations = yup.object({
    firstName: yup.string().defined('First name is required'),
    lastName: yup.string().defined('Last name is required'),
    dateOfBirth: yup
      .string()
      .defined('Birthdate is required')
      .test(
        'is-valid-date',
        `Date of birth must be in format ${localizedDatePattern.toLowerCase()} and a valid date`,
        (value = '') => !!localizedParseDate(value),
      )
      .test(
        'is-before-now',
        'Date of birth must be before today',
        (value = '') => {
          const asDate = localizedParseDate(value)
          return asDate && isPast(asDate) && !isToday(asDate)
        },
      )
      .test('is-after-year-1900', 'Year must be after 1900', (value = '') => {
        const asDate = localizedParseDate(value)
        return asDate && getYear(asDate) >= 1900
      }),
    cellPhoneLocal: yup
      .string()
      .transform((value) => value.replace(/[()-\s]/g, '')) // strip parenthesis, space, and dash
      .length(10, 'Cell phone should be 10 numbers')
      .matches(/^[0-9]{10}$/, 'Please check the cell phone')
      .defined('Cell phone is required to send assessments'),
  })

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
    setFieldError,
    isSubmitting,
    isValid,
    submitForm,
  } = useFormik<VerifyPatientForm>({
    initialValues: {
      firstName: '',
      lastName: '',
      cellPhoneLocal: '',
      dateOfBirth: '',
    },
    validationSchema: patientValidations,
    onSubmit: (values) => {
      // Remove () and -
      values.cellPhoneLocal = values.cellPhoneLocal.replace(/[()-\s]/g, '')
      const dob = localizedParseDate(values.dateOfBirth)

      if (!dob) return
      return verifyPatientData
        .mutateAsync({
          firstName: values.firstName,
          lastName: values.lastName,
          cellPhoneLocal: values.cellPhoneLocal,
          dateOfBirth: format(dob, 'yyyy-MM-dd'),
        })
        .then((result) => {
          if (result.patient.id) {
            push(
              PatientSelfOnboardUrls.existingPatientDetails.create(
                result.patient.id,
              ),
            )
          }
        })
        .catch(() => {
          setFieldError(
            'firstName',
            'Patient not found. Please verify data and try again.',
          )
        })
    },
  })

  return (
    <>
      <Box
        justifyContent="space-between"
        sx={{
          maxWidth: '787px',
          width: '100%',
          height: '100%',
        }}
      >
        <MuiStack justifyContent="center" className={classes.inputStack}>
          <Typography className={classes.title}>
            Enter patient details
          </Typography>
          <TextField
            id="patient-verify-first-name"
            label="First Name"
            name="firstName"
            required
            fullWidth
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.firstName && Boolean(errors.firstName)}
            helperText={touched.firstName && errors.firstName}
          />
          <TextField
            id="patient-verify-last-name"
            label="Last Name"
            name="lastName"
            required
            fullWidth
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.lastName && Boolean(errors.lastName)}
            helperText={touched.lastName && errors.lastName}
          />
          <DatePicker
            id="patient-verify-date-of-birth"
            label="Date of Birth"
            name="dateOfBirth"
            // defaultDate={isUsa ? '01/31/1990' : '31/01/1990'}
            inputFormat={isUsa ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
            required
            fullWidth
            maxDate={endOfYesterday()}
            isEditing={values.dateOfBirth ? values.dateOfBirth : undefined}
            setFieldValue={setFieldValue}
            onBlur={handleBlur}
            error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
            helperText={touched.dateOfBirth && errors.dateOfBirth}
          />
          <PhoneField
            id="patient-verify-cell-phone"
            label="Cell Phone"
            name="cellPhoneLocal"
            required
            fullWidth
            value={values.cellPhoneLocal}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.cellPhoneLocal && Boolean(errors.cellPhoneLocal)}
            helperText={touched.cellPhoneLocal && errors.cellPhoneLocal}
          />
        </MuiStack>
        <MuiStack
          direction="row"
          justifyContent="center"
          sx={{ marginTop: '40px', marginBottom: '40px' }}
        >
          <Button
            className={classes.goBackButton}
            disableElevation
            fullWidth
            style={{ marginRight: '20px' }}
            onClick={goBack}
          >
            Go Back
          </Button>
          {/* TODO: change submit form to validation and change step */}
          <Button
            variant="contained"
            disableElevation
            fullWidth
            loading={isSubmitting}
            disabled={!isValid}
            onClick={() => {
              submitForm()
            }}
          >
            Continue
          </Button>
        </MuiStack>
      </Box>
    </>
  )
}

export default VerifyPatient
